/**
 * Types for RepeatedAppointment entity management
 * Used in: RepeatedAppointmentSetup, EditRecurringSetupDialog
 * Supports both package-based (one-time) and subscription-based recurring setups
 */

import type { VehicleDetail } from "./recurring-appointments.types";

/**
 * Package details included in repeated appointment response
 */
export interface PackageDetail {
  id: string;
  name: string;
  price: number;
}

/**
 * Subscription details included in repeated appointment response
 */
export interface SubscriptionDetail {
  id: string;
  subscriptionId: string;
  subscription?: {
    id: string;
    name: string;
    price: number;
  };
}

/**
 * Input for creating a new RepeatedAppointment
 * Must have either packageId OR userSubscriptionId (not both)
 */
export interface RepeatedAppointmentInput {
  customerId: string;
  packageId?: string | null; // For one-time package setups
  userSubscriptionId?: string | null; // For subscription-based setups
  vehicleIds: string[];
  preferredDay?: string | null;
  preferredTime?: string | null;
}

/**
 * Input for updating an existing RepeatedAppointment
 */
export interface RepeatedAppointmentUpdateInput {
  preferredDay?: string | null;
  preferredTime?: string | null;
  vehicleIds?: string[];
  packageId?: string | null;
  userSubscriptionId?: string | null;
}

/**
 * Full RepeatedAppointment with expanded details
 */
export interface RepeatedAppointmentWithDetails {
  id: string;
  customerId: string;
  packageId: string | null;
  userSubscriptionId: string | null;
  preferredDay: string | null;
  preferredTime: string | null;
  vehicleIds: string[];
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  package?: PackageDetail | null;
  userSubscription?: SubscriptionDetail | null;
  vehicles?: VehicleDetail[];
  appointmentCount?: number;
}

/**
 * API Response for single RepeatedAppointment
 */
export interface RepeatedAppointmentSingleResponse {
  success: boolean;
  message: string;
  data: RepeatedAppointmentWithDetails;
}

/**
 * API Response for list of RepeatedAppointments
 */
export interface RepeatedAppointmentListResponse {
  success: boolean;
  data: RepeatedAppointmentWithDetails[];
}

/**
 * Days of the week for preferred day selection
 */
export const WEEKDAYS = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
] as const;

export type WeekDay = (typeof WEEKDAYS)[number]["value"];
